/**
 * Layout Constants for Templates Feature
 *
 * Centralized layout dimensions, spacing values, and z-indices
 * used across the templates interface.
 */

/**
 * Fixed dimensions for major layout sections
 */
export const LAYOUT_DIMENSIONS = {
  /** Width of the sidebar in pixels */
  SIDEBAR_WIDTH: 320,

  /** Minimum width of the sidebar in pixels */
  SIDEBAR_MIN_WIDTH: 280,

  /** Maximum width for template editor content in pixels */
  EDITOR_MAX_WIDTH: 800,

  /** Height of the top bar in pixels */
  TOP_BAR_HEIGHT: 64,

  /** Height of the bottom action bar in pixels */
  BOTTOM_BAR_HEIGHT: 72,

  /** Height of section cards */
  SECTION_CARD_HEIGHT: 80,

  /** Height of the "Add Section" card */
  ADD_SECTION_HEIGHT: 48,
} as const;

/**
 * Responsive spacing values for layout elements
 */
export const LAYOUT_SPACING = {
  /** Horizontal padding for main content area */
  CONTENT_HORIZONTAL_PADDING: {
    xs: 16,
    sm: 24,
    md: 32,
  },

  /** Vertical padding for main content area */
  CONTENT_VERTICAL_PADDING: {
    xs: 16,
    sm: 24,
    md: 32,
  },

  /** Gap between sidebar items */
  SIDEBAR_ITEM_GAP: 8,

  /** Padding inside sidebar */
  SIDEBAR_PADDING: 16,

  /** Gap between section cards */
  SECTION_CARD_GAP: 12,

  /** Horizontal margin for centered content */
  CONTENT_HORIZONTAL_MARGIN: 'auto',
} as const;

/**
 * Computed maxWidth values that include padding
 */
export const EDITOR_MAX_WIDTH_WITH_PADDING = {
  xs: LAYOUT_DIMENSIONS.EDITOR_MAX_WIDTH + 16 * 2, // 832px
  sm: LAYOUT_DIMENSIONS.EDITOR_MAX_WIDTH + 24 * 2, // 848px
  md: LAYOUT_DIMENSIONS.EDITOR_MAX_WIDTH + 32 * 2, // 864px
} as const;

/**
 * Z-index values for layered components
 */
export const LAYOUT_Z_INDEX = {
  /** Z-index for sidebar */
  SIDEBAR: 100,

  /** Z-index for top bar */
  TOP_BAR: 200,

  /** Z-index for bottom bar */
  BOTTOM_BAR: 200,

  /** Z-index for modals */
  MODAL: 1300,

  /** Z-index for dropdowns/popovers */
  DROPDOWN: 1400,
} as const;

/**
 * Animation timing and easing for layout transitions
 */
export const LAYOUT_ANIMATIONS = {
  /** Duration for sidebar slide animations in milliseconds */
  SIDEBAR_SLIDE_DURATION: 225,

  /** CSS easing function for smooth slide animations */
  SIDEBAR_SLIDE_EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',

  /** Duration for section reorder animations in milliseconds */
  SECTION_REORDER_DURATION: 200,

  /** Duration for expand/collapse animations */
  EXPAND_COLLAPSE_DURATION: 300,
} as const;

/**
 * Breakpoint-specific values for responsive design
 */
export const RESPONSIVE_BREAKPOINTS = {
  /** Sidebar collapses to drawer below this width */
  SIDEBAR_COLLAPSE: 'md',

  /** Show compact view below this width */
  COMPACT_VIEW: 'sm',
} as const;
